import React from 'react';
import { getPrimaryImagePathFromOrderItem, lineQty, pickCardAccentBorder, pickRowBackground } from './adminOrderScannerPickUtils';
import { ScannerPickImageWithLightbox } from './ScannerPickImageWithLightbox';
import { WrongScanDetails } from './ScannerPickWrongScanDisplay';

/**
 * Мобільна модалка збірки сканером: картки замість таблиці, зручне скролування та липке поле сканування.
 */
const AdminOrderScannerPickModalMobile = ({ pick, order, onHide }) => {
  const {
    inputRef,
    barcode,
    setBarcode,
    scannedCounts,
    wrongScans,
    submitScan,
    handlePaste,
    removeWrong,
    items,
    perfectPick,
    allExpectedDone,
    hasWrong,
    pickLoading,
  } = pick;

  return (
    <div
      className='modal fade show admin-order-scanner-pick-mobile'
      tabIndex='-1'
      role='dialog'
      aria-modal='true'
      style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onHide();
      }}
    >
      <div
        className='modal-dialog modal-dialog-scrollable m-2 mx-auto'
        role='document'
        style={{ maxWidth: '560px', width: 'calc(100% - 1rem)' }}
      >
        <div className='modal-content' style={{ borderRadius: '12px', overflow: 'hidden' }}>
          <div className='modal-header py-2 px-3 align-items-center border-bottom'>
            <h5 className='modal-title font-weight-bold mb-0 pr-2' style={{ fontSize: '1rem', lineHeight: 1.3 }}>
              Збірка сканером #{order.orderId}
            </h5>
            <button type='button' className='close p-2 m-0' aria-label='Закрити' onClick={onHide}>
              <span aria-hidden='true'>&times;</span>
            </button>
          </div>

          <div
            className='modal-body p-0 d-flex flex-column'
            style={{ maxHeight: 'calc(100vh - 5rem)', minHeight: 0 }}
          >
            <div className='px-3 pt-2 pb-0 flex-shrink-0'>
              {perfectPick && (
                <div className='alert alert-success py-2 px-2 mb-2 small' role='alert'>
                  <i className='bi bi-check-circle-fill mr-1' />
                  Усі позиції відскановано, збірка збігається з замовленням.
                </div>
              )}
              {allExpectedDone && hasWrong && (
                <div className='alert alert-warning py-2 px-2 mb-2 small' role='alert'>
                  <i className='bi bi-exclamation-triangle-fill mr-1' />
                  Замовлення зібране, але є зайві сканування.
                </div>
              )}
            </div>

            <div
              className='px-3 pb-2 flex-shrink-0 border-bottom bg-white'
              style={{
                position: 'sticky',
                top: 0,
                zIndex: 4,
                boxShadow: '0 4px 8px rgba(0,0,0,0.06)',
              }}
            >
              <input
                ref={inputRef}
                type='text'
                className='form-control'
                aria-label='Поле сканування штрихкоду'
                style={{ height: '52px', fontSize: '1.15rem' }}
                placeholder='Скануйте або вставте код'
                value={barcode}
                disabled={pickLoading}
                autoComplete='off'
                onPaste={handlePaste}
                onChange={(e) => {
                  const value = e.target.value || '';
                  if (value.includes('\n') || value.includes('\r')) {
                    const normalized = value.replace(/\r/g, '\n');
                    const firstCode = normalized.split('\n').find((part) => part.trim() !== '');
                    if (firstCode) submitScan(firstCode.trim());
                    setBarcode('');
                    return;
                  }
                  setBarcode(value);
                }}
                onKeyDown={(e) => e.key === 'Enter' && submitScan()}
              />
            </div>

            <div
              className='flex-grow-1 overflow-auto px-3 py-2'
              style={{ WebkitOverflowScrolling: 'touch', minHeight: 0 }}
            >
              <div className='font-weight-bold small text-muted mb-2'>Товари ({items.length})</div>
              <div className='d-flex flex-column' style={{ gap: '10px' }}>
                {items.map((item, index) => {
                  const need = lineQty(item);
                  const got = scannedCounts[index] || 0;
                  const bg = pickRowBackground(index, items, scannedCounts);
                  const accent = pickCardAccentBorder(index, items, scannedCounts);
                  const imgPath = getPrimaryImagePathFromOrderItem(item);
                  return (
                    <div
                      key={index}
                      className='rounded border shadow-sm'
                      style={{
                        background: bg,
                        borderLeft: `4px solid ${accent}`,
                        overflow: 'hidden',
                      }}
                    >
                      <div className='p-2 d-flex align-items-start' style={{ gap: 10 }}>
                        <ScannerPickImageWithLightbox imagePath={imgPath} alt={item.name || ''} size={64}
                          personalDiscount={item.personalDiscount} productDiscount={item.productDiscount}
                          price={item.price} discountedPrice={item.discountedPrice} />
                        <div className='flex-grow-1' style={{ minWidth: 0 }}>
                          <div className='d-flex justify-content-between align-items-start mb-1' style={{ gap: '8px' }}>
                            <div className='font-weight-bold' style={{ fontSize: '0.95rem', lineHeight: 1.35 }}>
                              {item.name}
                            </div>
                            <span
                              className='badge badge-pill flex-shrink-0 font-weight-bold'
                              style={{
                                fontSize: '0.85rem',
                                background:
                                  got >= need ? '#28a745' : got > 0 ? '#ffc107' : '#6c757d',
                                color: got > 0 && got < need ? '#212529' : '#fff',
                                padding: '0.35em 0.65em',
                              }}
                            >
                              {got}/{need}
                            </span>
                          </div>
                          <div className='small text-muted mb-0'>
                            <span className='font-weight-bold text-secondary'>Артикул:</span>{' '}
                            <span className='text-monospace'>{item.productCode || '—'}</span>
                          </div>
                          <div className='small text-muted mb-0 mt-1'>
                            <span className='font-weight-bold text-secondary'>Штрихкод:</span>{' '}
                            <span className='text-monospace'>{item.barcode || '—'}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasWrong ? (
                <div className='mt-4 pb-1'>
                  <div className='font-weight-bold small text-danger mb-2'>Не з замовлення</div>
                  <div className='d-flex flex-column' style={{ gap: '8px' }}>
                    {wrongScans.map((w) => (
                      <div
                        key={w.id}
                        className='d-flex align-items-start justify-content-between rounded border p-2'
                        style={{
                          background: '#f8d7da',
                          borderColor: '#f5c6cb',
                        }}
                      >
                        <div className='pr-2' style={{ minWidth: 0 }}>
                          <WrongScanDetails w={w} />
                        </div>
                        <button
                          type='button'
                          className='btn btn-sm btn-outline-danger flex-shrink-0 py-0 px-2 mt-0'
                          onClick={() => removeWrong(w.id)}
                          title='Прибрати'
                          aria-label='Прибрати зі списку'
                        >
                          <i className='bi bi-trash' />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>

          <div className='modal-footer py-2 px-3 border-top bg-light'>
            <button type='button' className='btn btn-dark btn-block font-weight-bold' onClick={onHide}>
              Закрити
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminOrderScannerPickModalMobile;
